import React, { useState } from "react";
import {
  YStack,
  XStack,
  Image,
  SizableText,
  H3,
  H4,
  Card,
  Separator,
  Button,
  Spinner
} from "tamagui";
import { useQuery } from "@tanstack/react-query";
import { MaterialIcons } from "@expo/vector-icons";
import * as Animatable from 'react-native-animatable';
import { View, Linking, Dimensions, ScrollView } from "react-native";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import apis from "@api/api";
import type { UIComponentContract } from "../../../../../../../types/componentContract";

// Component semantic contract for spec-driven resolution
export const uiContract: UIComponentContract = {
  id: "linkedin.profile",
  accepts: {
    capability: "read_profile",
    shape: {
      id: "string",
      localizedFirstName: "string",
      localizedLastName: "string",
      headline: "string?",
      profilePicture: "object",
      vanityName: "string?",
      connectionsSize: "number"
    },
    required: ["id", "localizedFirstName", "localizedLastName"],
    optional: ["headline", "profilePicture", "vanityName", "connectionsSize"]
  },
  renders: "card",
  priority: 10,
  surface: "options"
};

const safeText = (value: any): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value?.localized && value?.preferredLocale) {
    const localeKey = `${value.preferredLocale.language}_${value.preferredLocale.country}`;
    return value.localized[localeKey] || '';
  }
  return '';
};

const LinkedInOptions = () => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['linkedInProfile'],
    queryFn: async () => await apis.getLinkedInProfile(),
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const profile = data?.profile || {};
  const profileImageUri =
    data?.profile?.profilePicture?.["displayImage~"]?.elements?.[0]?.identifiers?.[0]?.identifier;

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Error State */}
          {error && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#fef2f2" : "#450a0a"}
              borderColor={theme ? "#fecaca" : "#7f1d1d"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <View style={{
                  backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                </View>
                <H3 color="#ef4444" fontWeight="600" textAlign="center">
                  Failed to load LinkedIn profile
                </H3>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  textAlign="center"
                >
                  Please try reconnecting your LinkedIn account, or try removing and re-adding the integration.   
                </SizableText>
                <Button
                  backgroundColor="#ef4444"
                  color="white"
                  onPress={() => refetch()}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="refresh" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Retry Connection
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Loading State */}
        {isLoading && (
          <Animatable.View animation="fadeIn" duration={600}>
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$6"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <Spinner size="large" color="#0A66C2" />
                <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                  Loading LinkedIn profile...
                </H4>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Profile Data */}
        {profile?.id && (
          <>
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                {/* Header with Profile Info */}
                <XStack alignItems="center" gap="$4" marginBottom="$4">
                  {profileImageUri ? (
                    <View style={{
                      width: 70,
                      height: 70,
                      borderRadius: 35,
                      overflow: 'hidden',
                      backgroundColor: '#0A66C2'
                    }}>
                      <Image
                        source={{ uri: profileImageUri }}
                        width={70}
                        height={70}
                      />
                    </View>
                  ) : (
                    <View style={{
                      width: 70,
                      height: 70,
                      borderRadius: 35,
                      backgroundColor: '#0A66C2',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <SizableText color="white" fontSize="$9" fontWeight="bold">
                        {profile.localizedFirstName?.charAt(0)?.toUpperCase() || 'L'}
                      </SizableText>
                    </View>
                  )}

                  <YStack flex={1}>
                    <H4
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="700"
                      numberOfLines={1}
                    >
                    {profile.localizedFirstName || "First"} {profile.localizedLastName || "Last"}
                    </H4>
                    <SizableText
                      size="$4"
                      color={theme ? "#64748b" : "#94a3b8"}
                      numberOfLines={2}
                    >
                    {safeText(profile.headline) || "No headline provided"}
                  </SizableText>
                </YStack>
              </XStack>

                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

                {/* Profile Details */}
                <YStack gap="$3">
                  <XStack alignItems="center" gap="$3">
                    <View style={{
                      backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialIcons name="badge" size={20} color="#0A66C2" />
                    </View>
                    <YStack flex={1}>
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Profile ID
                      </SizableText>
                      <SizableText
                        size="$3"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="600"
                        numberOfLines={1}
                      >
                        {profile.id}
                      </SizableText>
                    </YStack>
                  </XStack>

                  {safeText(profile.vanityName) && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="link" size={20} color="#0A66C2" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Vanity URL
                        </SizableText>
                        <SizableText
                          size="$4"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                        >
                          {safeText(profile.vanityName)}
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}

                  {data?.connectedDate && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="event" size={20} color="#0A66C2" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Connected On
                        </SizableText>
                        <SizableText
                          size="$4"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                        >
                          {new Date(data.connectedDate).toLocaleDateString()}
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}

                  {data?.connectionsSize !== null && data?.connectionsSize !== undefined && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="people" size={20} color="#0A66C2" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          1st-Degree Connections
                        </SizableText>
                        <SizableText
                          size="$4"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                        >
                          {data.connectionsSize?.toLocaleString() || '0'} connections
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}

                  <XStack alignItems="center" gap="$3">
                    <View style={{
                      backgroundColor: theme ? "#d1fae5" : "#065f46",
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialIcons
                        name={data?.authorised ? "check-circle" : "cancel"}
                        size={20}
                        color={data?.authorised ? "#10b981" : "#ef4444"}
                      />
                    </View>
                    <YStack flex={1}>
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Authorization Status
                      </SizableText>
                      <SizableText
                        size="$4"
                        color={data?.authorised ? "#10b981" : "#ef4444"}
                        fontWeight="600"
                      >
                        {data?.authorised ? "Authorized" : "Not Authorized"}
                      </SizableText>
                    </YStack>
                  </XStack>
                </YStack>

                {/* Action Button */}
                <Button
                  backgroundColor="#0A66C2"
                  color="white"
                  marginTop="$4"
                  onPress={async () => {
                    const vanityName = safeText(profile.vanityName);
                    const url = vanityName
                      ? `https://linkedin.com/in/${vanityName}`
                      : `https://linkedin.com`;
                    try {
                      await Linking.openURL(url);
                    } catch (error) {
                      console.error("Error opening LinkedIn profile:", error);
                    }
                  }}
                  borderRadius={12}
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="open-in-new" size={18} color="white" />
                    <SizableText color="white" fontWeight="600" size="$4">
                      View Profile on LinkedIn
                    </SizableText>
                  </XStack>
                </Button>
              </Card>
            </Animatable.View>

            {/* API Capabilities Section */}
            <Animatable.View animation="fadeInUp" duration={600} delay={200}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <H4
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  fontWeight="700"
                  marginBottom="$4"
                >
                  Available Capabilities
                </H4>
                <YStack gap="$3">
                  {/* Personal Profile Posting */}
                  <XStack alignItems="flex-start" gap="$3">
                    <View style={{
                      backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialIcons name="post-add" size={20} color="#0A66C2" />
                    </View>
                    <YStack flex={1}>
                      <SizableText
                        size="$3"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="600"
                      >
                        Post to Personal Profile
                      </SizableText>
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Create and share posts on your LinkedIn profile
                      </SizableText>
                    </YStack>
                  </XStack>

                  {/* Organization Management */}
                  <XStack alignItems="flex-start" gap="$3">
                    <View style={{
                      backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialIcons name="business" size={20} color="#0A66C2" />
                    </View>
                    <YStack flex={1}>
                      <SizableText
                        size="$3"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="600"
                      >
                        Manage Organization Pages
                      </SizableText>
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Post to company pages and manage organization content
                      </SizableText>
                    </YStack>
                  </XStack>

                  {/* Read Organization Data */}
                  <XStack alignItems="flex-start" gap="$3">
                    <View style={{
                      backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialIcons name="analytics" size={20} color="#0A66C2" />
                    </View>
                    <YStack flex={1}>
                      <SizableText
                        size="$3"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="600"
                      >
                        Access Organization Analytics
                      </SizableText>
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        View posts, comments, reactions, and engagement data
                      </SizableText>
                    </YStack>
                  </XStack>

                  {/* Profile Information */}
                  <XStack alignItems="flex-start" gap="$3">
                    <View style={{
                      backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialIcons name="person" size={20} color="#0A66C2" />
                    </View>
                    <YStack flex={1}>
                      <SizableText
                        size="$3"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="600"
                      >
                        Profile & Email Access
                      </SizableText>
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Access your profile information and email address
                      </SizableText>
                    </YStack>
                  </XStack>
                </YStack>
              </Card>
            </Animatable.View>

            {/* Success Message */}
            <Animatable.View animation="fadeInUp" duration={600} delay={300}>
              <Card
                backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                borderColor={theme ? "#dcfce7" : "#065f46"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack alignItems="center" gap="$3">
                  <View style={{
                    backgroundColor: theme ? "#d1fae5" : "#065f46",
                    borderRadius: 50,
                    padding: 12
                  }}>
                    <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                  </View>
                  <H4
                    color={theme ? "#059669" : "#10b981"}
                    fontWeight="600"
                    textAlign="center"
                  >
                    LinkedIn Connected Successfully!
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#065f46" : "#34d399"}
                    textAlign="center"
                  >
                    You can now post to your profile, manage organization pages, and access LinkedIn analytics directly from SmartSaaS.
                  </SizableText>
              </YStack>
              </Card>
            </Animatable.View>
            </>
          )}
      </YStack>
    </ErrorBoundary>
  );
};

// Attach contract to component for registry discovery
LinkedInOptions.uiContract = uiContract;

export default LinkedInOptions;
